<template>
  <Modal
    class="timer-modal"
    v-if="modals.timer && !session.isSpectator"
    @close="toggleModal('timer')"
  >
    <h3>{{ $t("timer.title") }}</h3>

    <template v-if="timer.status === 'idle' || timer.status === 'finished'">
      <div class="presets" :aria-label="$t('timer.presets')">
        <button
          v-for="preset in presets"
          :key="preset"
          type="button"
          :class="{ selected: durationSeconds === preset * 60 }"
          @click="setPreset(preset)"
        >
          {{ preset }} {{ $t("timer.minutesShort") }}
        </button>
      </div>
    </template>

    <div class="active-time">{{ displayedTime }}</div>

    <div class="time-adjustments" :aria-label="$t('timer.adjustTime')">
      <div class="time-adjustment">
        <button
          type="button"
          :title="$t('timer.subtractMinute')"
          :aria-label="$t('timer.subtractMinute')"
          :disabled="!canDecreaseTime"
          @click="adjustTime(-60)"
        >
          <font-awesome-icon icon="minus-circle" />
        </button>
        <span>1 {{ $t("timer.minutesShort") }}</span>
        <button
          type="button"
          :title="$t('timer.addMinute')"
          :aria-label="$t('timer.addMinute')"
          :disabled="!canIncreaseTime"
          @click="adjustTime(60)"
        >
          <font-awesome-icon icon="plus-circle" />
        </button>
      </div>

      <div class="time-adjustment">
        <button
          type="button"
          :title="$t('timer.subtractThirtySeconds')"
          :aria-label="$t('timer.subtractThirtySeconds')"
          :disabled="!canDecreaseTime"
          @click="adjustTime(-30)"
        >
          <font-awesome-icon icon="minus-circle" />
        </button>
        <span>30 {{ $t("timer.secondsShort") }}</span>
        <button
          type="button"
          :title="$t('timer.addThirtySeconds')"
          :aria-label="$t('timer.addThirtySeconds')"
          :disabled="!canIncreaseTime"
          @click="adjustTime(30)"
        >
          <font-awesome-icon icon="plus-circle" />
        </button>
      </div>
    </div>

    <label class="sound-option">
      <input
        type="checkbox"
        :checked="timer.soundEnabled"
        @change="setSound($event.target.checked)"
      />
      <font-awesome-icon
        :icon="timer.soundEnabled ? 'volume-up' : 'volume-mute'"
      />
      {{ $t("timer.sound") }}
    </label>

    <div class="timer-actions">
      <template v-if="timer.status === 'idle' || timer.status === 'finished'">
        <button
          class="button townsfolk"
          type="button"
          :disabled="!durationSeconds"
          @click="start"
        >
          <font-awesome-icon icon="play" /> {{ $t("timer.start") }}
        </button>
        <button
          v-if="timer.status === 'finished'"
          class="button demon"
          type="button"
          @click="stop"
        >
          <font-awesome-icon icon="times-circle" /> {{ $t("timer.close") }}
        </button>
      </template>

      <template v-else>
        <button
          v-if="timer.status === 'running'"
          class="button townsfolk"
          type="button"
          @click="pause"
        >
          <font-awesome-icon icon="pause" /> {{ $t("timer.pause") }}
        </button>
        <button v-else class="button townsfolk" type="button" @click="resume">
          <font-awesome-icon icon="play" /> {{ $t("timer.resume") }}
        </button>
        <button class="button demon" type="button" @click="stop">
          <font-awesome-icon icon="stop-circle" /> {{ $t("timer.stop") }}
        </button>
      </template>
    </div>
  </Modal>
</template>

<script>
import Modal from "./Modal";
import { mapMutations, mapState } from "vuex";

export default {
  components: { Modal },
  computed: {
    timer() {
      return this.session.timer;
    },
    durationSeconds() {
      return this.minutes * 60 + this.seconds;
    },
    isConfiguring() {
      return this.timer.status === "idle" || this.timer.status === "finished";
    },
    remainingMilliseconds() {
      return this.timer.status === "running"
        ? Math.max(0, this.timer.endAt - this.now)
        : Math.max(0, this.timer.remainingMs || 0);
    },
    displayedTime() {
      const totalSeconds = this.isConfiguring
        ? this.durationSeconds
        : Math.ceil(this.remainingMilliseconds / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
    },
    adjustableMilliseconds() {
      return this.isConfiguring
        ? this.durationSeconds * 1000
        : this.remainingMilliseconds;
    },
    canDecreaseTime() {
      return this.adjustableMilliseconds > 0;
    },
    canIncreaseTime() {
      return this.adjustableMilliseconds < 99 * 60 * 1000 + 30 * 1000;
    },
    ...mapState(["modals", "session"]),
  },
  data() {
    return {
      minutes: 5,
      seconds: 0,
      presets: [1, 3, 5, 7, 10],
      now: Date.now(),
      ticker: null,
    };
  },
  mounted() {
    this.loadDuration();
    this.ticker = setInterval(() => {
      this.now = Date.now();
    }, 250);
  },
  beforeDestroy() {
    clearInterval(this.ticker);
  },
  methods: {
    loadDuration() {
      const duration = Math.min(
        99 * 60 + 30,
        Math.max(
          30,
          Math.round((this.timer.durationMs || 5 * 60 * 1000) / 30000) * 30,
        ),
      );
      this.minutes = Math.floor(duration / 60);
      this.seconds = duration % 60;
    },
    setPreset(minutes) {
      this.minutes = minutes;
      this.seconds = 0;
    },
    start() {
      if (this.session.isSpectator || !this.durationSeconds) return;
      const durationMs = this.durationSeconds * 1000;
      this.$store.commit("session/setTimer", {
        status: "running",
        durationMs,
        remainingMs: durationMs,
        endAt: Date.now() + durationMs,
        soundEnabled: this.timer.soundEnabled,
        finishedAt: 0,
      });
      this.toggleModal("timer");
    },
    pause() {
      if (this.session.isSpectator || this.timer.status !== "running") return;
      this.$store.commit("session/setTimer", {
        ...this.timer,
        status: "paused",
        remainingMs: Math.max(0, this.timer.endAt - Date.now()),
        endAt: 0,
      });
    },
    resume() {
      if (this.session.isSpectator || this.timer.status !== "paused") return;
      const remainingMs = Math.max(1000, this.timer.remainingMs);
      this.$store.commit("session/setTimer", {
        ...this.timer,
        status: "running",
        remainingMs,
        endAt: Date.now() + remainingMs,
        finishedAt: 0,
      });
    },
    adjustTime(seconds) {
      if (this.session.isSpectator) return;
      const maximumMs = 99 * 60 * 1000 + 30 * 1000;

      if (this.isConfiguring) {
        const totalSeconds = Math.min(
          maximumMs / 1000,
          Math.max(0, this.durationSeconds + seconds),
        );
        this.minutes = Math.floor(totalSeconds / 60);
        this.seconds = totalSeconds % 60;
        return;
      }

      const remainingMs = Math.min(
        maximumMs,
        Math.max(0, this.remainingMilliseconds + seconds * 1000),
      );
      if (!remainingMs) {
        this.$store.commit("session/setTimer", {
          ...this.timer,
          status: "finished",
          remainingMs: 0,
          endAt: 0,
          finishedAt: Date.now(),
        });
        return;
      }
      this.$store.commit("session/setTimer", {
        ...this.timer,
        remainingMs,
        endAt: this.timer.status === "running" ? Date.now() + remainingMs : 0,
      });
    },
    stop() {
      if (this.session.isSpectator) return;
      this.$store.commit("session/setTimer", {
        ...this.timer,
        status: "idle",
        remainingMs: this.timer.durationMs,
        endAt: 0,
        finishedAt: 0,
      });
      this.toggleModal("timer");
    },
    setSound(soundEnabled) {
      if (this.session.isSpectator) return;
      this.$store.commit("session/setTimer", {
        ...this.timer,
        soundEnabled,
      });
    },
    ...mapMutations(["toggleModal"]),
  },
  watch: {
    "modals.timer"(shown) {
      if (shown) this.loadDuration();
    },
  },
};
</script>

<style lang="scss" scoped>
.timer-modal {
  h3 {
    margin: 0 35px 18px;
  }
}

.presets,
.timer-actions,
.time-adjustments,
.sound-option {
  display: flex;
  align-items: center;
  justify-content: center;
}

.presets {
  gap: 8px;
  margin-bottom: 18px;

  button {
    padding: 5px 12px;
    color: white;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 8px;
    cursor: pointer;

    &.selected,
    &:hover {
      color: #ffd15c;
      border-color: #ffd15c;
    }
  }
}

.active-time {
  margin: 8px 0 14px;
  font: bold 2.6em monospace;
  text-align: center;
}

.time-adjustments {
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 18px;
}

.time-adjustment {
  display: grid;
  grid-template-columns: 38px minmax(68px, auto) 38px;
  align-items: center;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.38);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 9px;

  button {
    height: 38px;
    padding: 0;
    color: white;
    background: rgba(255, 255, 255, 0.08);
    border: 0;
    cursor: pointer;

    &:first-child {
      border-right: 1px solid rgba(255, 255, 255, 0.25);
    }

    &:last-child {
      border-left: 1px solid rgba(255, 255, 255, 0.25);
    }

    &:hover:not(:disabled) {
      color: #ffd15c;
      background: rgba(255, 255, 255, 0.14);
    }

    &:disabled {
      color: gray;
      cursor: default;
      opacity: 0.45;
    }
  }

  span {
    padding: 0 8px;
    font-size: 0.85em;
    text-align: center;
    white-space: nowrap;
  }
}

.sound-option {
  gap: 8px;
  margin: 10px 0 18px;
  cursor: pointer;
}

.timer-actions {
  flex-wrap: wrap;
  gap: 4px;

  .button {
    font: inherit;
  }

  .button:disabled {
    color: gray;
    cursor: default;
    opacity: 0.6;
  }
}

@media screen and (max-width: 575.98px) {
  .presets {
    flex-wrap: wrap;
  }

  .time-adjustments {
    gap: 8px;
  }

  .timer-actions {
    flex-direction: column;
  }
}
</style>
